import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton, Badge, Menu, Box, Typography, Stack, Button, Divider, MenuItem
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { useNotifications } from "@/hooks/useNotifications";
import type { AppNotification } from "@/api/notifications";

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  function handleClick(notification: AppNotification) {
    if (!notification.isRead) markRead(notification.id);
    setAnchorEl(null);
    if (notification.linkUrl) navigate(notification.linkUrl);
  }

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Notifications">
        <Badge badgeContent={unreadCount} color="secondary" max={99}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={() => markAllRead()}>Mark all read</Button>
          )}
        </Stack>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">You're all caught up.</Typography>
          </Box>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => handleClick(n)}
              sx={{ whiteSpace: "normal", alignItems: "flex-start", py: 1.25, bgcolor: n.isRead ? "transparent" : "action.hover" }}
            >
              <Box sx={{ width: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {!n.isRead && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />}
                  <Typography variant="body2" fontWeight={n.isRead ? 400 : 600}>{n.title}</Typography>
                </Stack>
                {n.message && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: n.isRead ? 0 : 2 }}>
                    {n.message}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: n.isRead ? 0 : 2 }}>
                  {timeAgo(n.createdAt)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
